import React, { useState, useEffect } from "react";
import { Icon } from '@iconify/react/dist/iconify.js';
import toast from "react-hot-toast";
import { getSliderApi, createSliderApi, updateSliderApi, deleteSliderApi, ImageUrl } from "../../helper/apiHelper";

const SliderManagement = () => {
    const [leftSliders, setLeftSliders] = useState([]);
    const [rightSliders, setRightSliders] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [newSlider, setNewSlider] = useState({
        id: '',
        media: '',
        mediaType: 'image',
        title: '',
        subtitle: '',
        tagLine: '',
        index: '' // 0 for Left, 1 for Right
    });

    useEffect(() => {
        fetchSliders();
    }, []);

    const fetchSliders = async () => {
        setIsLoading(true);
        try {
            const response = await getSliderApi();
            if (response.data) {
                // Helper to map and sort sliders
                const mapSliders = (list) => list.map(slider => ({
                    id: slider.id,
                    media: `${ImageUrl}/${slider.asset}`,
                    mediaType: slider.isVideo ? 'video' : 'image',
                    title: slider.title,
                    subtitle: slider.subTitle,
                    tagLine: slider.tagLine || '',
                    index: slider.index,
                    position: slider.position
                })).sort((a, b) => a.position - b.position);

                if (response.data.leftSlider) {
                    setLeftSliders(mapSliders(response.data.leftSlider));
                }
                if (response.data.rightSlider) {
                    setRightSliders(mapSliders(response.data.rightSlider));
                }
            }
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to fetch sliders",
                { id: "fetch-sliders-error" }
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!newSlider.title) {
            toast.error("Please enter a title", { id: "title-required" });
            return;
        }

        if (!newSlider.subtitle) {
            toast.error("Please enter a subtitle", { id: "subtitle-required" });
            return;
        }

        if (!isEditing && !selectedFile) {
            toast.error("Please upload an image or video", { id: "file-required" });
            return;
        }

        setSaveLoading(true);
        try {
            const formData = new FormData();
            formData.append('index', newSlider.index);
            formData.append('title', newSlider.title);
            formData.append('subTitle', newSlider.subtitle);
            formData.append('tagLine', newSlider.tagLine);

            if (selectedFile) {
                formData.append('asset', selectedFile);
            }

            if (isEditing) {
                await updateSliderApi(formData, newSlider.id);
                toast.success("Slider updated successfully", { id: "slider-success" });
            } else {
                await createSliderApi(formData);
                toast.success("Slider added successfully", { id: "slider-success" });
            }

            setShowModal(false);
            resetForm();
            fetchSliders(); // Refresh the list
        } catch (error) {
            toast.error(
                error ||
                "Operation failed",
                { id: "slider-error" }
            );
        } finally {
            setSaveLoading(false);
        }
    };

    const resetForm = () => {
        setNewSlider({ id: '', media: '', mediaType: 'image', title: '', subtitle: '', tagLine: '', index: '' });
        setSelectedFile(null);
        setIsEditing(false);
    };

    const handleAddClick = (indexVal) => {
        resetForm();
        setNewSlider(prev => ({ ...prev, index: indexVal }));
        setShowModal(true);
    };

    const handleEdit = (item) => {
        setIsEditing(true);
        setNewSlider(item);
        setShowModal(true);
    };

    const handleDeleteClick = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;

        setIsLoading(true);
        try {
            await deleteSliderApi(deleteId);
            toast.success("Slider deleted successfully", { id: "delete-success" });
            setShowDeleteModal(false);
            setDeleteId(null);
            fetchSliders(); // Refresh the list
        } catch (error) {
            toast.error(error || "Failed to delete slider", { id: "delete-error" });
        } finally {
            setIsLoading(false);
        }
    };
const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
        // Check if it's a video and validate size (20MB limit)
        if (file.type.startsWith('video') && file.size > 20 * 1024 * 1024) {
            toast.error("Video size should be less than 20MB", { id: "file-size-error" });
            e.target.value = null;
            return;
        }
        
        setSelectedFile(file);
        const mediaUrl = URL.createObjectURL(file);
        const type = file.type.startsWith('video') ? 'video' : 'image';
        setNewSlider({ ...newSlider, media: mediaUrl, mediaType: type });
    }
};

    // Render a single slider card
    const renderSliderCard = (item) => (
        <div className="col-12" key={item.id}>
            <div className="hover-scale-img border radius-16 overflow-hidden p-8 position-relative group d-flex gap-3 align-items-center">
                <div className="radius-8 overflow-hidden position-relative" style={{ width: '120px', height: '80px', flexShrink: 0 }}>
                    {item.mediaType === 'video' ? (
                        <video
                            src={item.media}
                            className="w-100 h-100 object-fit-cover"
                        />
                    ) : (
                        <img
                            src={item.media}
                            alt={item.title}
                            className="hover-scale-img__img w-100 h-100 object-fit-cover"
                        />
                    )}
                </div>
                <div className="flex-grow-1">
                    <h6 className="text-sm fw-semibold mb-1 text-line-1">{item.title}</h6>
                    <p className="text-xs text-secondary-light text-line-1 mb-0">{item.subtitle}</p>
                </div>
                <div className="d-flex gap-2 pe-2">
                    <button
                        onClick={() => handleEdit(item)}
                        className="w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle bg-base text-primary-600 border border-primary-100 hover-bg-primary-50 transition-2"
                        title="Edit"
                    >
                        <Icon icon="lucide:edit" className="text-md" />
                    </button>
                    <button
                        onClick={() => handleDeleteClick(item.id)}
                        className="w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle bg-base text-danger-600 border border-danger-100 hover-bg-danger-50 transition-2"
                        title="Delete"
                    >
                        <Icon icon="fluent:delete-24-regular" className="text-md" />
                    </button>
                </div>
            </div>
        </div>
    );

    // Render the upload placeholder
    const renderUploadPlaceholder = (onClick, label) => (
        <div
            onClick={onClick}
            className="cursor-pointer border border-dashed border-primary-600 radius-16 bg-primary-50 hover-bg-primary-100 transition-2 d-flex flex-column align-items-center justify-content-center gap-2 p-24 mb-24"
            style={{ minHeight: '400px' }}
        >
            <div className="w-48-px h-48-px rounded-circle bg-white d-flex justify-content-center align-items-center text-primary-600 shadow-sm">
                <Icon icon="ic:baseline-plus" className="text-2xl" />
            </div>
            <h6 className="text-primary-600 fw-semibold mb-0">{label}</h6>
            <p className="text-secondary-light text-sm text-center mb-0">
                Click here to add a new slider to this section
            </p>
        </div>
    );

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden gallery-scale">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-lg fw-semibold mb-0">Slider Management</h6>
            </div>

            <div className="card-body p-24">
                {isLoading ? (
                    <div className="text-center py-40">
                        <div className="spinner-border text-primary" role="status" />
                    </div>
                ) : (
                    <div className="row gy-4">
                        {/* Left Section - Index 0 */}
                        <div className="col-lg-6 border-end">
                            <h5 className="mb-16 fw-bold text-primary-600">Left Section</h5>
                            {renderUploadPlaceholder(() => handleAddClick(0), "Add Left Slider")}

                            <div className="d-flex flex-column gap-3 mt-4">
                                {leftSliders.length > 0 ? (
                                    leftSliders.map(renderSliderCard)
                                ) : (
                                    <div className="text-center text-muted py-2">No sliders added yet</div>
                                )}
                            </div>
                        </div>

                        {/* Right Section - Index 1 */}
                        <div className="col-lg-6">
                            <h5 className="mb-16 fw-bold text-primary-600">Right Section</h5>
                            {renderUploadPlaceholder(() => handleAddClick(1), "Add Right Slider")}

                            <div className="d-flex flex-column gap-3 mt-4">
                                {rightSliders.length > 0 ? (
                                    rightSliders.map(renderSliderCard)
                                ) : (
                                    <div className="text-center text-muted py-2">No sliders added yet</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block' }}>
                        <div className="modal-dialog modal-md modal-dialog-centered" style={{ zIndex: 11122 }}>
                            <div className="modal-content radius-16 bg-base">
                                <div className="modal-header py-16 px-24 border-bottom">
                                    <h1 className="modal-title fs-5">
                                        {isEditing ? "Edit Slider" : "Add Slider"}
                                        <span className="text-sm fw-normal text-secondary-light ms-2">
                                            ({newSlider.index == 0 ? "Left Section" : "Right Section"})
                                        </span>
                                    </h1>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
                                </div>
                                <div className="modal-body p-24">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Title</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter title"
                                            value={newSlider.title}
                                            onChange={(e) => setNewSlider({ ...newSlider, title: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Subtitle</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter subtitle"
                                            value={newSlider.subtitle}
                                            onChange={(e) => setNewSlider({ ...newSlider, subtitle: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Tag Line</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter tag line"
                                            value={newSlider.tagLine}
                                            onChange={(e) => setNewSlider({ ...newSlider, tagLine: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Upload Video or Image</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="image/*,video/*"
                                            onChange={handleFileChange}
                                        />
                                        <small className="text-secondary-light">Supported formats: Images and Videos</small>
                                    </div>
                                    {newSlider.media && (
                                        <div className="mb-3">
                                            {newSlider.mediaType === 'video' ? (
                                                <video
                                                    src={newSlider.media}
                                                    className="w-100 h-100 object-fit-cover radius-8"
                                                    style={{ maxHeight: '200px' }}
                                                    controls
                                                />
                                            ) : (
                                                <img
                                                    src={newSlider.media}
                                                    alt="Preview"
                                                    className="w-100 h-100 object-fit-cover radius-8"
                                                    style={{ maxHeight: '200px' }}
                                                />
                                            )}
                                        </div>
                                    )}
                                    <div className="d-flex align-items-center justify-content-end gap-3 mt-24">
                                        <button
                                            type="button"
                                            className="btn btn-secondary border border-secondary-600 text-md px-24 py-10 radius-8"
                                            onClick={() => setShowModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary border border-primary-600 text-md px-32 py-10 radius-8"
                                            onClick={handleSave}
                                            disabled={saveLoading}
                                        >
                                            {isEditing ? 'Update' : 'Save'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
                </>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <>
                    <div className="modal fade show" style={{ display: 'block' }}>
                        <div className="modal-dialog modal-dialog-centered" style={{ zIndex: 11122 }}>
                            <div className="modal-content radius-16 bg-base">
                                <div className="modal-header py-16 px-24 border-bottom">
                                    <h1 className="modal-title fs-5">Confirm Delete</h1>
                                    <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
                                </div>
                                <div className="modal-body p-24">
                                    <p className="text-center mb-0">Are you sure you want to delete this slider?</p>
                                </div>
                                <div className="modal-footer py-16 px-24 border-top">
                                    <button
                                        type="button"
                                        className="btn btn-secondary border border-secondary-600 text-md px-24 py-10 radius-8"
                                        onClick={() => setShowDeleteModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger border border-danger-600 text-md px-32 py-10 radius-8"
                                        onClick={confirmDelete}
                                        disabled={isLoading}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
                </>
            )}
        </div>
    );
};

export default SliderManagement;
